import React, {useCallback, useMemo} from 'react';
import {type SxProps} from '@mui/material';
import {Box} from '@mui/system';
import assert from 'assert-ts';
import {useNavigate} from 'react-router';
import {type AgentSubType, Concept} from 'types';
import type {Agent} from 'api/types';
import type {Data} from 'schemaDefinition/types';
import {ColorPalette} from 'theme';
import {useAgentsCodelistIds} from 'services/agent';
import {
  useGetAgentQuery,
  useMergeAgentsMutation,
} from 'services/agent/agentApi';
import {useCodelists} from 'services/codelists';
import {getActualDataChangesWithSchema} from 'services/metadata/functions/getActualDataChangesWithSchema';
import {assertAgentValue, isAgentValue} from 'services/utils';
import {mapRtkErrorToHttpError} from 'store/api/mapRtkErrorToHttpError';
import {ActionButton, Card, FlexBox, Text, useSnacks} from 'components';
import {RtkContent} from 'components/rtkContent/RtkContent';
import {DataFormV1} from 'schema';
import {DataFormCompareLayout} from 'schema/form/components/DataFormCompareLayout';
import {validate} from 'schema/form/functions/validators';
import {useSchema} from 'schemas';
import {useCollectionFormConfiguration} from 'editors/collections/hooks/useCollectionFormConfiguration';

const primaryColorSx: SxProps = {
  color: ColorPalette.primary.burgundy,
};

type Props = {
  agentId: string;
  otherId: string;
};
export const CompareAgents: React.FC<Props> = ({agentId, otherId}) => {
  const navigate = useNavigate();
  const codelistIds = useAgentsCodelistIds();
  const codelistMap = useCodelists(codelistIds);
  const {errorSnack, warningSnack, successSnack} = useSnacks();
  const {
    data: agentData,
    status: agentStatus,
    error: agentError,
  } = useGetAgentQuery({
    id: agentId,
  });
  const {
    data: otherData,
    status: otherStatus,
    error: otherError,
  } = useGetAgentQuery({
    id: otherId,
  });
  const [mergeAgents] = useMergeAgentsMutation();

  const formConfiguration = useCollectionFormConfiguration();

  const agentSubType: AgentSubType = agentData?.agentType ?? Concept.person;

  const schema = useSchema({
    entity: Concept.agent,
    subType: agentSubType,
  });

  const {actualChanges, trimmedSchema} = useMemo(() => {
    const changes =
      agentData && otherData
        ? getActualDataChangesWithSchema(
            otherData,
            agentData,
            schema,
            undefined,
            {
              excludeEmpty: true,
              excludeEqual: true,
              excludeReadOnly: false,
            },
          )
        : undefined;

    return {
      ...changes,
    };
  }, [otherData, agentData, schema]);

  const [editedData, setEditedData] = React.useState<Agent | undefined>(
    agentData,
  );
  const onDataChanged = useCallback((data: unknown) => {
    if (isAgentValue(data)) {
      setEditedData(data);
    } else {
      setEditedData(undefined);
    }
  }, []);

  const handleSave = useCallback((): Promise<void> => {
    const validation = validate(
      editedData as Data,
      undefined,
      schema,
      assert(codelistMap, 'CompareAgents: codelistMap is required'),
    );

    if (validation.valid !== 'valid') {
      if (validation.valid === 'warning') {
        warningSnack(validation.errors.map(e => e.value).join(', '));
      } else {
        if (validation.errors.filter(e => e.value).length > 0) {
          errorSnack(validation.errors.map(e => e.value).join(', '));
        }
        return Promise.resolve();
      }
    }

    return mergeAgents({
      target: assertAgentValue(editedData),
      source: assertAgentValue(otherData),
    })
      .unwrap()
      .then(() => {
        successSnack('Agenter ble slått sammen');
        navigate('overview');
        return Promise.resolve();
      })
      .catch(error => {
        errorSnack('En feil oppstod!', mapRtkErrorToHttpError(error));
      });
  }, [
    editedData,
    schema,
    codelistMap,
    mergeAgents,
    otherData,
    warningSnack,
    errorSnack,
    successSnack,
    navigate,
  ]);

  return (
    <>
      <FlexBox horizontal right width={'100%'}>
        <ActionButton title={'Slå sammen agenter'} onClick={handleSave} />
      </FlexBox>
      <FlexBox horizontal pb={1} px={2} flex={1}>
        <DataFormCompareLayout
          renderCurrent={() => (
            <>
              <Text variant="h4" sx={primaryColorSx} textTransform="uppercase">
                Agent som beholdes
              </Text>
              <Text variant="caption" sx={primaryColorSx}>
                Denne agenten vil få alle koblingene, og eventuelle endringer
                som gjøres.
              </Text>
            </>
          )}
          renderProposed={() => (
            <Box ml={2}>
              <Text variant="h4" sx={primaryColorSx} textTransform="uppercase">
                Agent som slettes
              </Text>
              <Text variant="caption" sx={primaryColorSx}>
                Koblingene vil bli flyttet fra denne agenten til den andre
                agenten, og denne agenten vil bli slettet etterpå.
              </Text>
            </Box>
          )}
        />
      </FlexBox>
      <FlexBox horizontal pb={1} px={2} flex={1}>
        <DataFormCompareLayout
          renderCurrent={() => (
            <RtkContent status={agentStatus} error={agentError}>
              <DataFormV1
                id={'mergedData1'}
                schema={schema}
                originalData={agentData}
                data={agentData ?? {}}
                configuration={formConfiguration}
                mode="edit"
                relatedData={{}}
                showErrors={true}
                onDataChanged={onDataChanged}
              />
            </RtkContent>
          )}
          renderProposed={() => (
            <RtkContent status={otherStatus} error={otherError}>
              {trimmedSchema ? (
                <DataFormV1
                  id={'mergedData2'}
                  schema={trimmedSchema}
                  data={actualChanges ?? {}}
                  configuration={formConfiguration}
                  mode="read-only"
                  relatedData={{}}
                />
              ) : (
                <Card>Ingen forskjeller</Card>
              )}
            </RtkContent>
          )}
        />
      </FlexBox>
    </>
  );
};
